import { Auth } from 'aws-amplify';
import constants from '../config';

export default class AuthService {
  constructor(domain) {
    this.domain = domain || constants.DOMAIN;
    this.fetch = this.fetch.bind(this);
    this.login = this.login.bind(this);
    this.getProfile = this.getProfile.bind(this);
  }

  // Sign up via Cognito, returns { needsConfirmation, cognitoSub }
  async signup(name, userName, email, password, country) {
    // Use UUID as Cognito username (works with all pool configurations)
    // Email is stored as attribute and used for login
    var cognitoUsername = crypto.randomUUID();

    var attributes = {
      email: email.toLowerCase().trim(),
      name: name,
    };
    // Prod Cognito pool supports preferred_username as a signup attribute;
    // dev/staging pool has it configured as an alias, which blocks it for unconfirmed users
    if (process.env.NODE_ENV === 'production' && process.env.REACT_APP_STAGING !== 'true') {
      attributes.preferred_username = userName;
    }

    var result = await Auth.signUp({
      username: email.toLowerCase().trim(),
      password: password,
      attributes,
    });

    return {
      needsConfirmation: !result.userConfirmed,
      cognitoSub: result.userSub,
      email: email.toLowerCase().trim(),
      name: name,
      userName: userName,
      country: country,
    };
  }

  // Confirm signup with code (uses email as Cognito username)
  async confirmSignUp(email, code) {
    await Auth.confirmSignUp(email.toLowerCase().trim(), code);
    return { confirmed: true };
  }

  // Resend confirmation code (uses email as Cognito username)
  async resendSignUp(email) {
    await Auth.resendSignUp(email.toLowerCase().trim());
    return { sent: true };
  }

  // Login via Cognito, then fetch and cache MongoDB profile.
  // If /me fails (orphaned Cognito user with no MongoDB record), auto-create via cognito-signup.
  async login(email, password) {
    var cognitoEmail = email.toLowerCase().trim();

    var user = await Auth.signIn(cognitoEmail, password);
    var session = user.signInUserSession;
    var idToken = session.idToken.jwtToken;

    // Cache token for synchronous access
    localStorage.setItem('primal_cognito_token', idToken);

    var profile;
    try {
      profile = await this._fetchProfile(idToken);
    } catch (err) {
      // /me failed — MongoDB user may not exist (orphaned Cognito account).
      // Recover by creating the MongoDB user from Cognito token data.
      var payload = session.idToken.payload;
      profile = await this.createMongoUser(
        payload.sub,
        payload.name || user.username,
        user.username,
        payload.email || cognitoEmail,
        'United States',
        idToken
      );
    }

    localStorage.setItem('primal_user_profile', JSON.stringify(profile));
    return profile;
  }

  // Create MongoDB user after Cognito signup confirmation.
  // Requires the Cognito ID token so the backend can verify the caller owns the cognitoSub.
  async createMongoUser(cognitoSub, name, userName, email, country, idToken) {
    var authHeader = idToken ? 'Bearer ' + idToken : '';
    var res = await fetch(this.domain + '/api/v1/users/cognito-signup', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader,
      },
      body: JSON.stringify({
        cognitoSub: cognitoSub,
        name: name,
        userName: userName,
        email: email.toLowerCase().trim(),
        country: country || 'United States',
      }),
    });

    if (!res.ok) {
      var data = await res.json().catch(function() { return {}; });
      throw new Error(data.error || 'Failed to create account');
    }

    return res.json();
  }

  // Fetch profile from backend using Cognito token
  async _fetchProfile(token) {
    var res = await fetch(this.domain + '/api/v1/users/me', {
      headers: { Authorization: 'Bearer ' + token },
    });

    if (!res.ok) {
      throw new Error('Failed to fetch profile');
    }

    return res.json();
  }

  post(title, text, images, price, category, shippingCost) {
    var data = new FormData();
    data.append('title', title);
    data.append('text', text);
    for (var i = 0; i < images.length; i++) {
      data.append('images', images[i], images[i].name);
    }
    data.append('price', price);
    data.append('category', category);
    data.append('shippingCost', shippingCost);

    return this.fetch(this.domain + '/api/v1/posts', {
      mode: 'cors',
      method: 'POST',
      body: data,
    }).then(function(response) {
      return Promise.resolve(response);
    });
  }

  loggedIn() {
    return localStorage.getItem('primal_cognito_token') && localStorage.getItem('primal_user_profile');
  }

  setAppToken(idToken) {
    localStorage.setItem('app_token', idToken);
  }

  getAppToken() {
    return localStorage.getItem('app_token');
  }

  setToken(idToken) {
    // Legacy compatibility — some components may still call this
    localStorage.setItem('primal_cognito_token', idToken);
  }

  getToken() {
    return localStorage.getItem('primal_cognito_token');
  }

  // Returns full Authorization header value with Bearer prefix (Cognito)
  getAuthHeader() {
    var token = this.getToken();
    if (!token) return '';
    return 'Bearer ' + token;
  }

  logout() {
    try {
      Auth.signOut();
    } catch (e) { /* ignore */ }
    localStorage.removeItem('primal_cognito_token');
    localStorage.removeItem('primal_user_profile');
    localStorage.removeItem('app_token');
    localStorage.removeItem('primal_verified');
  }

  getProfile() {
    try {
      var profileStr = localStorage.getItem('primal_user_profile');
      if (profileStr) return JSON.parse(profileStr);
    } catch (e) { /* ignore */ }
    return null;
  }

  getAppProfile() {
    // Primal Community app login — unchanged
    try {
      var decode = require('jwt-decode').default;
      return decode(this.getAppToken());
    } catch (e) { return null; }
  }

  // Forgot password — Cognito sends verification code
  async forgotPassword(email) {
    await Auth.forgotPassword(email.toLowerCase().trim());
    return { sent: true };
  }

  // Confirm forgot password with code + new password
  async forgotPasswordSubmit(email, code, newPassword) {
    await Auth.forgotPasswordSubmit(email.toLowerCase().trim(), code, newPassword);
    return { success: true };
  }

  fetch(url, options) {
    var headers = {
      'Access-Control-Allow-Origin': '*',
    };

    if (options && options.headers) {
      Object.keys(options.headers).forEach(function(key) { headers[key] = options.headers[key]; });
    }

    var authService = this;

    if (this.loggedIn()) {
      headers['Authorization'] = this.getAuthHeader();
    }

    return fetch(url, Object.assign({}, options, { headers: headers }))
      .then(function(response) {
        if (response.status === 401 && authService.loggedIn()) {
          // Token may be expired — try refreshing
          return authService._refreshToken().then(function() {
            headers['Authorization'] = authService.getAuthHeader();
            return fetch(url, Object.assign({}, options, { headers: headers }));
          }).then(function(retryResponse) {
            return authService._checkStatus(retryResponse);
          }).catch(function() {
            // Refresh failed — log out
            authService.logout();
            window.dispatchEvent(new Event('authStateChanged'));
            var error = new Error('Session expired');
            error.response = response;
            throw error;
          });
        }
        return authService._checkStatus(response);
      })
      .then(function(response) { return response.json(); });
  }

  async _refreshToken() {
    try {
      var session = await Auth.currentSession();
      var newToken = session.getIdToken().getJwtToken();
      localStorage.setItem('primal_cognito_token', newToken);
    } catch (e) {
      throw new Error('Token refresh failed');
    }
  }

  _checkStatus(response) {
    if (response.status >= 200 && response.status < 300) {
      return response;
    }
    var error = new Error(response.statusText);
    error.response = response;
    throw error;
  }

  appFetch(url, options) {
    var headers = {
      'Access-Control-Allow-Origin': '*',
    };

    if (this.loggedIn()) {
      headers['Authorization'] = this.getAppToken();
    }

    if (options && options.headers) {
      Object.keys(options.headers).forEach(function(key) { headers[key] = options.headers[key]; });
    }

    return fetch(url, Object.assign({}, options, { headers: headers }))
      .then(this._checkStatus)
      .then(function(response) { return response.json(); });
  }

  appLogin(email, password) {
    var data = new URLSearchParams();
    data.append('email', email);
    data.append('password', password);
    return this.appFetch(constants.PRIMAL_APP_API + '/api/v1/users/login', {
      mode: 'cors',
      method: 'POST',
      body: data,
    }).then(function(response) {
      localStorage.setItem('app_token', response.token);
      return Promise.resolve(response);
    });
  }
}
